import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  VideoCamera,
  Fingerprint,
  Lightning,
  DesktopTower,
  WifiHigh,
  Wrench,
  Buildings,
  House,
  Storefront,
  Factory,
  Bank,
  GraduationCap,
  Hospital,
  ArrowRight,
  Phone,
  WhatsappLogo,
  EnvelopeSimple,
  CheckCircle,
  Headset,
  Certificate,
} from "@phosphor-icons/react";

const OFFICIAL_PHONE_DISPLAY = "+91 81232 40004";
const OFFICIAL_PHONE_TEL = "tel:+918123240004";
const OFFICIAL_PHONE_ALT_DISPLAY = "+91 89519 30080";
const OFFICIAL_PHONE_ALT_TEL = "tel:+918951930080";
const OFFICIAL_WHATSAPP_DISPLAY = "+91 76187 78223";
const OFFICIAL_WHATSAPP_URL = "https://wa.me/917618778223";
const OFFICIAL_EMAIL = "support@swiftechindia.com";
const OFFICIAL_EMAIL_HREF = `mailto:${OFFICIAL_EMAIL}`;
const OFFICIAL_LOGO_SRC = "/assets/swiftech-logo.jpg";
const OFFICIAL_LOGO_ALT = "Swiftech Electronic Security and Technology Solutions";

const Landing = ({ user, logout }) => {
  const navigate = useNavigate();

  const securityServices = [
    {
      icon: VideoCamera,
      title: "CCTV & Surveillance",
      desc: "CCTV cameras, DVR/NVR, remote monitoring and complete surveillance solutions.",
    },
    {
      icon: Fingerprint,
      title: "Access, Attendance & Safety",
      desc: "Biometric attendance, access control, video door phones and alarm systems.",
    },
    {
      icon: DesktopTower,
      title: "Automation & Smart Displays",
      desc: "Home automation, interactive displays, smart boards, TVs and display solutions.",
    },
    {
      icon: WifiHigh,
      title: "Networking & IT Infrastructure",
      desc: "Networking, Wi-Fi, servers, storage, laptops and related IT products.",
    },
    {
      icon: Lightning,
      title: "Power & System Reliability",
      desc: "UPS, power backup and supporting technology infrastructure for uninterrupted uptime.",
    },
    {
      icon: Wrench,
      title: "Installation, AMC & Dealer Supply",
      desc: "Installation, maintenance, AMC contracts, bulk supply and dealer / installer support.",
    },
  ];

  const industries = [
    { icon: House, label: "Residential & Villas" },
    { icon: Buildings, label: "Corporate Offices" },
    { icon: Storefront, label: "Retail & Showrooms" },
    { icon: Factory, label: "Factories & Warehouses" },
    { icon: Bank, label: "Banks & Financial" },
    { icon: GraduationCap, label: "Schools & Colleges" },
    { icon: Hospital, label: "Hospitals & Clinics" },
    { icon: Buildings, label: "Hotels & Hospitality" },
  ];

  // Priority brand names – widely recognised, publicly-known security & IT vendors.
  const brandLogos = [
    "Hikvision",
    "CP Plus",
    "Dahua",
    "Uniview",
    "Godrej",
    "Honeywell",
    "Bosch",
    "Matrix",
    "Zicom",
    "eSSL",
    "Realtime",
    "Panasonic",
  ];

  const whyPoints = [
    {
      icon: Certificate,
      title: "Multi-Brand Portfolio",
      desc: "A wide selection of products from leading global and Indian brands — one point of contact for the full portfolio.",
    },
    {
      icon: Wrench,
      title: "In-House Installation Team",
      desc: "In-house engineers for site survey, cabling, commissioning and configuration — coordinated by a single team.",
    },
    {
      icon: Headset,
      title: "Post-Sales Support & AMC",
      desc: "Dedicated helpdesk, on-site service and annual maintenance contracts to keep systems running.",
    },
    {
      icon: ShieldCheck,
      title: "End-to-End Design",
      desc: "Consultation-led design covering surveillance, access, automation, networking and IT infrastructure in one plan.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full backdrop-blur-xl bg-white/80 border-b border-slate-200 shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
          <div className="flex items-center" data-testid="brand-logo">
            <img
              src={OFFICIAL_LOGO_SRC}
              alt={OFFICIAL_LOGO_ALT}
              data-testid="header-logo-img"
              className="h-9 md:h-10 w-auto select-none"
              draggable={false}
            />
          </div>
          <nav className="hidden md:flex items-center gap-1" data-testid="public-nav">
            <button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300"
              data-testid="nav-services"
            >
              Services
            </button>
            <button
              onClick={() => document.getElementById("industries")?.scrollIntoView({ behavior: "smooth" })}
              className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300"
              data-testid="nav-industries"
            >
              Industries
            </button>
            <button
              onClick={() => document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" })}
              className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300"
              data-testid="nav-brands"
            >
              Brands
            </button>
            <button
              onClick={() => navigate("/products")}
              className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300"
              data-testid="nav-products"
            >
              Products
            </button>
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={OFFICIAL_PHONE_TEL}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2.5 text-sm font-medium transition-colors duration-300"
            >
              <Phone size={16} weight="bold" /> Call {OFFICIAL_PHONE_DISPLAY}
            </a>
            {user ? (
              <>
                <button
                  data-testid="dashboard-nav-btn"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-4 py-2.5 text-sm font-medium transition-colors duration-300"
                >
                  Dashboard
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-3 py-2.5 text-sm font-medium transition-colors duration-300"
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                data-testid="login-btn"
                onClick={() => navigate("/auth")}
                className="rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-4 py-2.5 text-sm font-medium transition-colors duration-300"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[72vh]">
            <div>
              <div className="inline-flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-slate-600 mb-6">
                <ShieldCheck size={14} weight="bold" className="text-[#002FA7]" />
                Serving India
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="hero-title"
              >
                Electronic Security Systems and Complete Technology Solutions
              </h1>
              <p className="text-base leading-relaxed text-slate-700 mb-8 max-w-xl" data-testid="hero-subtitle">
                Swiftech designs, installs and services multi-brand CCTV &amp; surveillance, access &amp; attendance, home automation, smart displays, networking, servers and IT infrastructure — for homes, businesses, dealers and installers across India. One trusted partner for products, projects, installation and support.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={OFFICIAL_PHONE_TEL}
                  data-testid="hero-call-btn"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 flex items-center gap-2"
                >
                  <Phone size={20} weight="bold" /> Call {OFFICIAL_PHONE_DISPLAY}
                </a>
                <a
                  href={OFFICIAL_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="rounded-none bg-[#25D366] text-white hover:bg-[#1eb856] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2"
                >
                  <WhatsappLogo size={20} weight="bold" /> WhatsApp Us
                </a>
                <button
                  data-testid="explore-products-btn"
                  onClick={() => navigate("/products")}
                  className="rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2"
                >
                  View Product Range <ArrowRight size={18} weight="bold" />
                </button>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
                <div>
                  <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Multi
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Brand Portfolio</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    All-India
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Positioning</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Dealer &amp;
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Installer Supply</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/28117882/pexels-photo-28117882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Electronic security and surveillance systems"
                className="w-full border border-slate-200 shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 md:py-28 px-6 md:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] mb-3">Our Services</h3>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold mb-4"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Complete electronic security &amp; technology solutions
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              End-to-end consultation, supply, installation, integration and maintenance – all handled by one accountable team.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
            {securityServices.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="bg-white border border-slate-200 rounded-none p-6 hover:shadow-md hover:border-[#002FA7]/40 transition-all duration-300"
                >
                  <Icon size={32} weight="duotone" className="text-[#002FA7] mb-3" />
                  <h4 className="text-base font-semibold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {s.title}
                  </h4>
                  <p className="text-sm text-slate-600">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] mb-3">Industries We Serve</h3>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Purpose-built security for every environment
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="industries-grid">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <div
                  key={ind.label}
                  className="border border-slate-200 rounded-none p-5 flex flex-col items-start gap-3 hover:border-[#002FA7]/40 hover:bg-slate-50 transition-all duration-300"
                >
                  <Icon size={26} weight="duotone" className="text-[#002FA7]" />
                  <span className="text-sm font-semibold text-slate-800">{ind.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section id="brands" className="py-20 md:py-28 px-6 md:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] mb-3">Brands We Work With</h3>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold mb-4"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Products from the brands you know
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We supply and support a wide selection of leading global and Indian manufacturers, with lifecycle service backed by the respective manufacturer&apos;s warranty terms.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="brands-grid">
            {brandLogos.map((b) => (
              <div
                key={b}
                className="bg-white border border-slate-200 rounded-none px-4 py-5 flex items-center justify-center text-slate-800 font-semibold text-sm hover:border-[#002FA7]/40 transition-all duration-300"
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Swiftech */}
      <section className="py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] mb-3">Why Swiftech</h3>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              One accountable partner for security &amp; technology
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="why-grid">
            {whyPoints.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="border border-slate-200 rounded-none p-6">
                  <Icon size={28} weight="duotone" className="text-[#002FA7] mb-3" />
                  <h4 className="text-base font-semibold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {w.title}
                  </h4>
                  <p className="text-sm text-slate-600">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="py-20 md:py-28 px-6 md:px-12 bg-[#002FA7]">
        <div className="max-w-5xl mx-auto text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold mb-4 text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Planning a security or technology upgrade?
          </h2>
          <p className="text-base leading-relaxed text-white/90 mb-8 max-w-2xl mx-auto">
            Talk to our team for a free consultation, site survey and quotation. Fast response, honest advice, transparent pricing.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={OFFICIAL_PHONE_TEL}
              data-testid="cta-call-btn"
              className="rounded-none bg-white text-[#002FA7] hover:bg-slate-100 px-6 py-3.5 font-semibold transition-colors duration-300 text-base flex items-center gap-2"
            >
              <Phone size={18} weight="bold" /> Call {OFFICIAL_PHONE_DISPLAY}
            </a>
            <a
              href={OFFICIAL_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="rounded-none bg-[#25D366] text-white hover:bg-[#1eb856] px-6 py-3.5 font-semibold transition-colors duration-300 text-base flex items-center gap-2"
            >
              <WhatsappLogo size={18} weight="bold" /> WhatsApp {OFFICIAL_WHATSAPP_DISPLAY}
            </a>
            <a
              href={OFFICIAL_EMAIL_HREF}
              data-testid="cta-email-btn"
              className="rounded-none bg-transparent border border-white/60 text-white hover:bg-white/10 px-6 py-3.5 font-semibold transition-colors duration-300 text-base flex items-center gap-2"
            >
              <EnvelopeSimple size={18} weight="bold" /> {OFFICIAL_EMAIL}
            </a>
          </div>
          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-white/80" data-testid="cta-benefits">
            <span className="inline-flex items-center gap-2">
              <CheckCircle size={16} weight="bold" /> Free consultation
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle size={16} weight="bold" /> Site survey on request
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle size={16} weight="bold" /> Post-installation support
            </span>
          </div>
          <div className="mt-6 text-sm text-white/80" data-testid="cta-alt-contact">
            <span className="uppercase tracking-[0.2em] text-xs text-white/60 mr-2">Alternative</span>
            <a
              href={OFFICIAL_PHONE_ALT_TEL}
              data-testid="cta-alt-call-link"
              className="underline underline-offset-4 hover:text-white transition-colors"
            >
              {OFFICIAL_PHONE_ALT_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-16 px-6 md:px-12" data-testid="site-footer">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="inline-flex bg-white p-2 rounded-sm mb-4" data-testid="footer-brand">
              <img
                src={OFFICIAL_LOGO_SRC}
                alt={OFFICIAL_LOGO_ALT}
                data-testid="footer-logo-img"
                className="h-10 w-auto select-none"
                draggable={false}
              />
            </div>
            <p className="text-sm text-slate-400 max-w-md">
              Electronic security systems and complete technology solutions – supplied, installed and serviced across India by Swiftech.
            </p>
          </div>
          <div>
            <h5 className="text-xs uppercase tracking-[0.2em] font-bold text-white mb-3">Contact</h5>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={OFFICIAL_PHONE_TEL}
                  data-testid="footer-call-link"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Phone size={14} /> {OFFICIAL_PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={OFFICIAL_PHONE_ALT_TEL}
                  data-testid="footer-alt-call-link"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Phone size={14} />
                  <span>
                    {OFFICIAL_PHONE_ALT_DISPLAY}
                    <span className="ml-2 text-xs uppercase tracking-[0.15em] text-slate-500">Alt</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={OFFICIAL_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <WhatsappLogo size={14} /> {OFFICIAL_WHATSAPP_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={OFFICIAL_EMAIL_HREF}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2 hover:text-white transition-colors"
                >
                  <EnvelopeSimple size={14} /> {OFFICIAL_EMAIL}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h5 className="text-xs uppercase tracking-[0.2em] font-bold text-white mb-3">Explore</h5>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                  className="hover:text-white transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById("industries")?.scrollIntoView({ behavior: "smooth" })}
                  className="hover:text-white transition-colors"
                >
                  Industries
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" })}
                  className="hover:text-white transition-colors"
                >
                  Brands
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/products")}
                  className="hover:text-white transition-colors"
                >
                  Product Range
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-slate-800 text-center text-xs text-slate-500">
          © 2026 Swiftech. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Landing;
